import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaPlus, FaMinus, FaArrowRight } from 'react-icons/fa';
import FAQSchema from '../SEO/FAQSchema';
import BouncingDots from '../Common/BouncingDots';

const DEFAULT_FAQS = [
  {
    question: 'What exactly does Cyborg Sapient do?',
    answer: 'We operate as a full-stack digital partner across three pillars: performance marketing (SEO, Google/Meta/YouTube Ads, email, WhatsApp), software engineering (SaaS products, websites, mobile apps), and AI automation (custom pipelines, workflow automation, analytics). Most clients work with us across all three — our integrated model removes the handoff friction between marketing and engineering.',
  },
  {
    question: 'How long before we see measurable results?',
    answer: 'It depends on the service. Paid media (Google/Meta Ads) typically produces measurable ROAS improvements within 2–4 weeks. SEO takes 3–6 months for significant organic growth, though technical fixes are indexed within days. Software projects follow a defined sprint timeline agreed at kickoff — most MVPs ship in 8–14 weeks.',
  },
  {
    question: 'What does your engagement model look like?',
    answer: 'We offer retainer-based engagements for ongoing marketing, and milestone-based project contracts for engineering work. Every engagement includes a dedicated account manager, a shared Slack channel, weekly video syncs, and monthly performance audits. There are no lock-in contracts — we operate on a 30-day rolling basis after the initial project period.',
  },
  {
    question: 'Do you work with businesses outside India?',
    answer: 'Yes. While headquartered in Bangalore, we run campaigns and engineering projects for clients across the US, UK, Australia, and the Middle East. Our team operates across time zones, and we have proven experience navigating platform-specific rules and audience behaviours in international markets.',
  },
  {
    question: 'Can you handle both marketing and software development simultaneously?',
    answer: 'This is where we differentiate most clearly. Our engineering and marketing teams work in the same sprint cycles. A campaign team can request a new landing page variant and have it built, A/B tested, and live within the same week — without going through a separate agency or internal dev queue.',
  },
  {
    question: 'What is your pricing model?',
    answer: 'Pricing is scope-based. Marketing retainers start from ₹30,000/month and scale with channel count and budget volume. Engineering projects are priced by deliverable scope after a free discovery session. We don\'t publish fixed price lists because every business has different requirements — contact us for a transparent, itemized quote.',
  },
];

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };

const FAQSection = ({ faqs = [] }) => {
  const [openIdx, setOpenIdx] = useState(null);
  const items = faqs.length > 0 ? faqs : DEFAULT_FAQS;

  return (
    <>
      <FAQSchema faqs={items} />
      <section className="w-full bg-[#FAFBF9] border-t border-[#F1F3F1] relative overflow-hidden">
        <BouncingDots />

        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-40 relative z-10">

          {/* Heading row */}
          <div className="lg:grid lg:grid-cols-[1fr_1.4fr] lg:gap-20 lg:items-start mb-16 sm:mb-24">
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
              variants={fadeUp} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
                ✦&nbsp;&nbsp;Common Questions&nbsp;&nbsp;✦
              </span>
              <h2 className="leading-[1.05] tracking-tight">
                <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">
                  Every answer,
                </span>
                <span className="block text-4xl sm:text-5xl md:text-[62px] font-bold text-black">
                  <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">right here.</span>
                </span>
              </h2>
              <p className="text-base text-[#5A635A] font-normal mt-7 leading-relaxed max-w-sm">
                Transparent answers on timelines, pricing, deliverables, and what it's actually like to work with us.
              </p>
            </motion.div>

            {/* Quick stats column */}
            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={fadeUp} transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:grid grid-cols-2 gap-4 content-start mt-14 lg:mt-0"
            >
              {[
                { n: '24 hr', l: 'Response SLA'       },
                { n: '30 day', l: 'Rolling Contracts'  },
                { n: '8–14 wk', l: 'Typical MVP Timeline' },
                { n: '4.9 / 5', l: 'Client Satisfaction'  },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-[#EAECE9] rounded-[18px] px-6 py-5">
                  <div className="text-2xl font-bold text-black tabular-nums">{s.n}</div>
                  <div className="text-[11px] text-[#5A635A] uppercase tracking-wider font-semibold mt-1">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Accordion */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={fadeUp} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="border-t border-[#EAECE9]"
          >
            {items.map((faq, i) => {
              const isOpen = openIdx === i;
              return (
                <div key={i} className="border-b border-[#EAECE9]">
                  <button
                    onClick={() => setOpenIdx(isOpen ? null : i)}
                    className="w-full py-6 sm:py-7 flex items-start justify-between gap-6 text-left group hover:opacity-80 transition-opacity focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-start gap-5 sm:gap-7">
                      <span className="text-[11px] font-semibold tabular-nums text-[#5A635A]/50 mt-1 flex-shrink-0 pt-0.5">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-base sm:text-lg font-semibold text-black leading-snug tracking-tight">
                        {faq.question}
                      </h3>
                    </div>
                    <div className="flex-shrink-0 mt-0.5 w-7 h-7 rounded-full border border-[#EAECE9] flex items-center justify-center text-black transition-colors duration-200 group-hover:border-[#1C2E1E]">
                      {isOpen ? <FaMinus size={9} /> : <FaPlus size={9} />}
                    </div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1, transition: { height: { duration: 0.38, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.25 } } }}
                        exit={{ height: 0, opacity: 0, transition: { height: { duration: 0.3, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.15 } } }}
                        className="overflow-hidden"
                      >
                        <div className="pl-12 sm:pl-16 pb-7">
                          <p className="text-[#5A635A] text-sm sm:text-base leading-relaxed font-normal max-w-2xl">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={fadeUp} transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 sm:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between"
          >
            <p className="text-sm text-[#5A635A] font-normal">
              Still have a specific question? Our team replies within 24 hours.
            </p>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 bg-black text-white rounded-full text-sm font-semibold hover:bg-[#1C2E1E] transition-colors duration-200">
                Contact Us Directly
                <FaArrowRight size={11} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FAQSection;
